import React, { forwardRef } from "react";
import { Box } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

const Container = styled(Box)(() => ({
	display: "flex",
	flexDirection: "column",
	span: {
		fontWeight: 900,
	},
}));

const resolveThemeColor = (theme, value) => {
	if (!value || typeof value !== "string") {
		return value;
	}

	if (value.startsWith("#") || value.startsWith("rgb")) {
		return value;
	}

	const segments = value.split(".");
	if (segments.length === 1) {
		return (
			theme.palette?.[segments[0]]?.main ||
			theme.customTokens?.colors?.[segments[0]] ||
			value
		);
	}

	const [group, shade] = segments;
	const numericShade = Number(shade);

	const paletteGroup = theme.palette?.[group];
	if (paletteGroup) {
		if (!Number.isNaN(numericShade) && paletteGroup[numericShade] !== undefined) {
			return paletteGroup[numericShade];
		}
		if (paletteGroup[shade] !== undefined) {
			return paletteGroup[shade];
		}
	}

	const customGroup = theme.customTokens?.colors?.[group];
	if (customGroup) {
		if (!Number.isNaN(numericShade) && customGroup[numericShade] !== undefined) {
			return customGroup[numericShade];
		}
		if (customGroup[shade] !== undefined) {
			return customGroup[shade];
		}
	}

	return value;
};

const resolvePadding = (theme, padding) => {
	if (padding === undefined || padding === null) {
		return undefined;
	}
	if (typeof padding === "number") {
		return theme.spacing(padding);
	}
	if (typeof padding === "string") {
		const numeric = Number(padding);
		if (!Number.isNaN(numeric)) {
			return theme.spacing(numeric);
		}
		try {
			const spacingValue = theme.spacing(padding);
			if (spacingValue !== undefined) {
				return spacingValue;
			}
		} catch (_) {
			return padding;
		}
	}
	return padding;
};

const variantOrderMap = {
	h1: 1,
	h2: 2,
	h3: 3,
	h4: 4,
	h5: 5,
	h6: 6,
};

const getNumericLineHeight = (lineHeight) => {
	if (typeof lineHeight === "number") {
		return lineHeight;
	}
	if (typeof lineHeight === "string") {
		const parsed = parseFloat(lineHeight);
		return Number.isNaN(parsed) ? null : parsed;
	}
	return null;
};

const getHeightValue = (fontSize, lineHeight, lines) => {
	if (!fontSize || !lineHeight) {
		return undefined;
	}
	const multiplier = lineHeight * lines;
	if (typeof fontSize === "number") {
		return fontSize * multiplier;
	}
	return `calc(${fontSize} * ${multiplier})`;
};

export const Display = forwardRef(
	(
		{
			children,
			order = 1,
			variant,
			c,
			bg,
			p = 0,
			fw,
			style = {},
			sx,
			useVariantFontFamily = false,
			...rest
		},
		ref
	) => {
	const theme = useTheme();
	const displayHeadings = theme.other?.displayHeadings || {};
	const displayFontFamily = displayHeadings.fontFamily || theme.typography?.fontFamily;
	const levelKey = `d${order}`;
	const displayVariant = displayHeadings[levelKey] || displayHeadings.d1 || {};
	const headingVariant = variant ? theme.typography?.[variant] : undefined;
	const activeVariant = headingVariant || displayVariant;
	const paddingValue = resolvePadding(theme, p);

	const resolvedFontFamily =
		useVariantFontFamily && headingVariant?.fontFamily
			? headingVariant.fontFamily
			: displayFontFamily;

	const computedStyles = {
		color: resolveThemeColor(theme, c),
		backgroundColor: resolveThemeColor(theme, bg),
		padding: paddingValue,
		fontFamily: resolvedFontFamily,
		fontSize: activeVariant?.fontSize || displayVariant?.fontSize,
		fontWeight: fw ?? activeVariant?.fontWeight ?? displayVariant?.fontWeight,
		lineHeight: activeVariant?.lineHeight || displayVariant?.lineHeight,
		letterSpacing: activeVariant?.letterSpacing || displayVariant?.letterSpacing,
		wordSpacing: activeVariant?.wordSpacing || displayVariant?.wordSpacing,
		textAlign: "left",
		textTransform: "capitalize",
		width: "fit-content",
		...(typeof style === "object" ? style : {}),
	};

	return (
		<Container ref={ref} sx={{ ...computedStyles, ...(sx || {}) }} {...rest}>
			{children}
		</Container>
	);
});
Display.displayName = "Display";

export const ThreadsTitle = forwardRef(
	(
		{
			variant = "h2",
			order,
			lines = 2,
			c,
			bg,
			p,
			fw,
			sx,
			style,
			children,
			...rest
		},
		ref
	) => {
		const theme = useTheme();
		const headingVariant = theme.typography?.[variant] || {};
		const resolvedOrder = order ?? variantOrderMap[variant] ?? 1;
		const numericLineHeight = getNumericLineHeight(headingVariant.lineHeight);
		const height = getHeightValue(headingVariant.fontSize, numericLineHeight, lines);

		return (
			<Display
				ref={ref}
				variant={variant}
				order={resolvedOrder}
				c={c}
				bg={bg}
				p={p}
				fw={fw}
				useVariantFontFamily={false}
				sx={{
					overflow: "hidden",
					display: "-webkit-box",
					WebkitLineClamp: lines,
					WebkitBoxOrient: "vertical",
					height,
					textTransform: "none",
					...(numericLineHeight ? { lineHeight: headingVariant.lineHeight } : {}),
					...(sx || {}),
				}}
				style={style}
				{...rest}
			>
				{children}
			</Display>
		);
	}
);
ThreadsTitle.displayName = "ThreadsTitle";

export const ThreadsPreview = forwardRef(
	(
		{
			variant = "body1",
			order,
			lines = 3,
			c,
			bg,
			p,
			fw,
			sx,
			style,
			children,
			...rest
		},
		ref
	) => {
		const theme = useTheme();
		const textVariant = theme.typography?.[variant] || {};
		const resolvedOrder = order ?? variantOrderMap[variant] ?? 5;
		const numericLineHeight = getNumericLineHeight(textVariant.lineHeight);
		const height = getHeightValue(textVariant.fontSize, numericLineHeight, lines);

		return (
			<Display
				ref={ref}
				variant={variant}
				order={resolvedOrder}
				c={c}
				bg={bg}
				p={p}
				fw={fw ?? textVariant.fontWeight ?? 400}
				useVariantFontFamily
				sx={{
					overflow: "hidden",
					display: "-webkit-box",
					WebkitLineClamp: lines,
					WebkitBoxOrient: "vertical",
					height,
					textTransform: "none",
					...(numericLineHeight ? { lineHeight: textVariant.lineHeight } : {}),
					...(sx || {}),
				}}
				style={style}
				{...rest}
			>
				{children}
			</Display>
		);
	}
);
ThreadsPreview.displayName = "ThreadsPreview";
